import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { GroupSeatData } from '../groupSubscriptionTypes';
import { isSeatBulkActionable } from './groupCard.utils';

type UseGroupSelectionResult = {
  selectedIds: Set<string>;
  hasSelection: boolean;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  /** True when at least one visible seat is bulk-actionable. When false, the
   *  header checkbox should render disabled — there's nothing to select. */
  hasActionableVisible: boolean;
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  toggleSeat: (seatId: string) => void;
  toggleAllVisible: () => void;
  clearSelection: () => void;
  selectedSeats: GroupSeatData[];
};

export function useGroupSelection(allSeats: GroupSeatData[], visibleSeats: GroupSeatData[]): UseGroupSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // Only actionable seats count toward the header checkbox state — selecting
  // an unactionable row would yield no bulk-button affordance, so excluding
  // them from "select all" keeps the checkbox honest.
  const actionableVisibleIds = useMemo(
    () => visibleSeats.filter(isSeatBulkActionable).map((s) => s.id),
    [visibleSeats],
  );
  const hasActionableVisible = actionableVisibleIds.length > 0;
  const allVisibleSelected = hasActionableVisible && actionableVisibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = actionableVisibleIds.some((id) => selectedIds.has(id));
  const hasSelection = selectedIds.size > 0;

  const toggleSeat = useCallback((seatId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(seatId)) next.delete(seatId);
      else next.add(seatId);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of actionableVisibleIds) next.delete(id);
      } else {
        for (const id of actionableVisibleIds) next.add(id);
      }
      return next;
    });
  }, [allVisibleSelected, actionableVisibleIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // The header checkbox needs imperative `indeterminate` state — there's no
  // controlled-prop equivalent in HTMLInputElement, so we sync via ref.
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const selectedSeats = useMemo(() => allSeats.filter((s) => selectedIds.has(s.id)), [allSeats, selectedIds]);

  return {
    selectedIds,
    hasSelection,
    allVisibleSelected,
    someVisibleSelected,
    hasActionableVisible,
    headerCheckboxRef,
    toggleSeat,
    toggleAllVisible,
    clearSelection,
    selectedSeats,
  };
}
